import { FkuItemPrice } from '@hl-itemlist/api/itemlist-api'

/**
 * カードに表示する価格の表示モデル。
 *
 * 見せ方はiOSアプリの一覧セル(FkuItemCellView)に合わせる:
 * - 「￥<価格>」+ 価格幅がある場合(hasRange)は末尾に「~」
 * - セール中は元値の取り消し線ではなく、「X% OFF」/「¥X,XXX OFF」の割引バッジを価格の隣に出す
 *   (displayPriceIncludingTax にはセール後の価格が入っている)
 * - 価格を表示できない商品は「￥-」表示({@link UNAVAILABLE_PRICE_VIEW})
 *
 * amountLabelは数値のみ(カンマ区切り)で、通貨記号は表示側(ProductCard)が付ける。
 * サイト共通の価格表示(currency込みのtoLocaleString + .gp-price--small-yenで記号を0.65emに縮小)は
 * カード一覧では記号が小さくなりすぎるため使わず、カード側で75%の独自スタイルを当てる。
 */
export interface ProductPriceView {
  readonly amountLabel: string
  readonly hasRange: boolean
  readonly discountLabel: string | null
}

/**
 * 価格を表示できない商品の表示(「￥-」)。
 * 表示価格が0(BFFが価格を解決できない商品)・価格APIのエラー・レスポンスに該当FKUが無い、の
 * いずれもこの表示に統一する。「-」単体では何の欄か分からないため、通貨記号は付けたままにする
 * (アプリの「-」表示とは意図的に異なる)。
 */
export const UNAVAILABLE_PRICE_VIEW: ProductPriceView = {
  amountLabel: '-',
  hasRange: false,
  discountLabel: null,
}

export function toProductPriceView(price: FkuItemPrice): ProductPriceView {
  const amount = Math.trunc(price.displayPriceIncludingTax)

  // 0円=価格を解決できない商品。範囲(~)や割引バッジも意味を持たないため「価格なし」表示に丸ごと落とす
  if (amount === 0) {
    return UNAVAILABLE_PRICE_VIEW
  }

  return {
    amountLabel: amount.toLocaleString('ja-JP'),
    hasRange: price.hasRange,
    discountLabel: buildDiscountLabel(price),
  }
}

function buildDiscountLabel(price: FkuItemPrice): string | null {
  if (!price.underSale || !price.discount) {
    return null
  }

  return price.discount.method === 'percentage'
    ? `${Math.trunc(price.discount.value)}% OFF`
    : `¥${Math.trunc(price.discount.value).toLocaleString('ja-JP')} OFF`
}
